<template>
  <div class="tw-flex flex-col rounded-2xl overflow-hidden border border-[#c6c5d2] shadow-sm bg-white" style="height: 580px;">
    <!-- Header -->
    <div class="bg-[#071c5b] px-5 py-3 tw-flex items-center gap-3 flex-shrink-0">
      <div class="w-8 h-8 rounded-full bg-white/10 tw-flex items-center justify-center">
        <span class="material-symbols-outlined text-white text-base">support_agent</span>
      </div>
      <div class="flex-1">
        <p class="text-white font-semibold text-sm m-0">{{ title }}</p>
        <p class="text-blue-200/70 text-xs m-0">{{ subtitle }}</p>
      </div>
      <button
        v-if="showClose"
        class="w-8 h-8 rounded-full bg-white/10 tw-flex items-center justify-center hover:bg-white/20 transition-colors border-none cursor-pointer"
        @click="$emit('close')"
      >
        <span class="material-symbols-outlined text-white text-base">close</span>
      </button>
    </div>

    <!-- Messages -->
    <div ref="messagesEl" class="flex-1 overflow-y-auto tw-flex flex-col gap-3 p-4 bg-[#f8f9fa]">
      <!-- Welcome state -->
      <div v-if="!messages.length" class="tw-flex flex-col items-center justify-center h-full gap-3 text-center py-8">
        <div class="w-14 h-14 rounded-full bg-[#dde1ff] tw-flex items-center justify-center">
          <span class="material-symbols-outlined text-[#071c5b] text-2xl">support_agent</span>
        </div>
        <p class="text-sm font-semibold text-[#191c1d] m-0">{{ welcomeTitle }}</p>
        <p class="text-xs text-slate-400 m-0 max-w-xs">{{ welcomeSubtitle }}</p>
      </div>

      <!-- Message bubbles -->
      <template v-else>
        <div
          v-for="msg in messages"
          :key="msg.id"
          class="tw-flex gap-2 text-left"
          :class="msg.role === 'user' ? 'justify-end' : 'justify-start'"
        >
          <div v-if="msg.role === 'assistant'" class="w-7 h-7 rounded-full bg-[#dde1ff] tw-flex items-center justify-center flex-shrink-0 mt-1">
            <span class="material-symbols-outlined text-[#071c5b] text-sm">support_agent</span>
          </div>
          <div
            v-if="msg.role === 'user'"
            class="max-w-[80%] px-3 py-2.5 rounded-2xl rounded-br-sm text-sm leading-relaxed bg-[#071c5b] text-white"
          >{{ msg.message }}
          </div>
          <div
            v-else
            class="max-w-[80%] px-3 py-2.5 rounded-2xl rounded-bl-sm text-sm leading-relaxed bg-white text-[#191c1d] shadow-sm border border-[#e8e9ec] chat-bubble-content"
            v-html="msg.message"
          ></div>
        </div>
      </template>

      <!-- Typing indicator -->
      <div v-if="isLoading" class="tw-flex gap-2 justify-start">
        <div class="w-7 h-7 rounded-full bg-[#dde1ff] tw-flex items-center justify-center flex-shrink-0 mt-1">
          <span class="material-symbols-outlined text-[#071c5b] text-sm">support_agent</span>
        </div>
        <div class="bg-white border border-[#e8e9ec] rounded-2xl rounded-bl-sm px-4 py-3 tw-flex gap-1 items-center shadow-sm">
          <span class="chat-dot"></span>
          <span class="chat-dot" style="animation-delay: 0.15s"></span>
          <span class="chat-dot" style="animation-delay: 0.3s"></span>
        </div>
      </div>
    </div>

    <!-- Input -->
    <div class="border-t border-[#c6c5d2] p-3 tw-flex gap-2 bg-white flex-shrink-0">
      <input
        ref="inputEl"
        v-model="inputText"
        type="text"
        class="flex-1 px-3 py-2 text-sm border border-[#c6c5d2] rounded-xl bg-[#f8f9fa] text-[#191c1d] placeholder-[#757681] focus:outline-none focus:border-[#071c5b] transition-colors"
        :placeholder="inputPlaceholder"
        :disabled="isLoading"
        @keydown.enter.prevent="send"
      />
      <button
        class="w-10 h-10 rounded-xl bg-[#071c5b] tw-flex items-center justify-center flex-shrink-0 border-none cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
        :disabled="!inputText.trim() || isLoading"
        @click="send"
      >
        <span class="material-symbols-outlined text-white text-base">send</span>
      </button>
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import { sendMessage } from '@/Domain/ConversationalAgent/Api/conversationalAgent'

  interface ChatMessage {
    id: string
    role: 'user' | 'assistant'
    message: string
  }

  export default defineComponent({
    name: 'FacilityFinderChat',
    props: {
      title: {
        type: String,
        default: 'Zorg & Welzijn Assistent',
      },
      subtitle: {
        type: String,
        default: 'Stel een vraag rond mogelijke gespecialiseerde zorgtrajecten voor een patient.',
      },
      welcomeTitle: {
        type: String,
        default: 'Hoe kan ik je helpen?',
      },
      welcomeSubtitle: {
        type: String,
        default: 'Stel een vraag rond mogelijke gespecialiseerde zorgtrajecten voor een patient.',
      },
      inputPlaceholder: {
        type: String,
        default: 'Typ je vraag...',
      },
      showClose: {
        type: Boolean,
        default: true,
      },
    },
    emits: ['close'],
    data() {
      return {
        messages: [] as ChatMessage[],
        sessionId: '' as string,
        isLoading: false,
        inputText: '',
      }
    },
    methods: {
      focusInput() {
        (this.$refs.inputEl as HTMLInputElement)?.focus()
      },
      async send() {
        const text = this.inputText.trim()
        if (!text || this.isLoading) return

        this.inputText = ''
        this.messages.push({ id: Date.now().toString(), role: 'user', message: text })
        this.isLoading = true
        await this.$nextTick()
        this.scrollToBottom()

        try {
          const res = await sendMessage(text, this.sessionId)
          // createJSONRequest returns the parsed JSON body directly (not axios-style {data: ...})
          if (res.id) this.sessionId = res.id
          this.messages.push({ id: (res.id ?? '') + '-' + Date.now(), role: 'assistant', message: res.message })
        } catch {
          this.messages.push({ id: 'err-' + Date.now(), role: 'assistant', message: 'Er is iets misgegaan. Probeer het opnieuw.' })
        } finally {
          this.isLoading = false
          await this.$nextTick()
          this.scrollToBottom()
          // Re-focus the input (it loses focus while disabled during loading)
          this.focusInput()
        }
      },
      scrollToBottom() {
        const el = this.$refs.messagesEl as HTMLElement
        if (el) el.scrollTop = el.scrollHeight
      },
    },
  })
</script>

<style scoped>
  .chat-dot {
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #9ca3af;
    animation: chat-bounce 0.6s infinite alternate;
  }

  @keyframes chat-bounce {
    0% {
      transform: translateY(0);
    }
    100% {
      transform: translateY(-4px);
    }
  }

  .chat-bubble-content :deep(p) {
    margin: 0.2rem 0;
  }

  .chat-bubble-content :deep(a) {
    color: #071c5b;
    text-decoration: underline;
  }

  .chat-bubble-content :deep(ul),
  .chat-bubble-content :deep(ol) {
    padding-left: 1.25rem;
    margin: 0.25rem 0;
  }
</style>
